import { createWorkbook, createWorksheet, exportWorkbook } from "../../ExportTemplateExcel/helperTemplateExcel";
import * as ExcelJS from 'exceljs';

interface ExportExcelTerminProps {
    source: string;
    data: any[];
    selectMultiPipeline: any;
    nameFile: string;
    startDate: string;
    endDate: string;
}

export const exportExcelTermin = ({ source, data, selectMultiPipeline, nameFile, startDate, endDate }: ExportExcelTerminProps) => {
    const workbook = createWorkbook();
    const worksheet = createWorksheet(workbook, source);

    let currentRow = 1;
    const totalColumns = 6; // Fixed columns: No, Nama Termin, Tanggal Jatuh Tempo, Tanggal Bayar, Nominal, Status Pembayaran
    const lastColumn = String.fromCharCode(65 + totalColumns);

    // Pipeline Header
    worksheet.mergeCells(`A${currentRow}:${lastColumn}${currentRow}`);
    const pipelineName = selectMultiPipeline.name || 'Unknown Pipeline';
    worksheet.getCell(`A${currentRow}`).value = `LAPORAN PIPELINE ${pipelineName.toUpperCase()}`;
    stylePipelineHeader(worksheet, currentRow, totalColumns);
    currentRow++;

    // Period
    worksheet.mergeCells(`A${currentRow}:${lastColumn}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = `Periode ${startDate} - ${endDate}`;
    stylePeriodHeader(worksheet, currentRow, totalColumns);
    currentRow++;

    // Empty row
    currentRow++;

    worksheet.getCell(`A${currentRow}`).value = 'Total';
    worksheet.getCell(`B${currentRow}`).value = `: ${data[0].totalNominalPipeline}`;
    styleInfoSection(worksheet, currentRow);
    currentRow++;

    currentRow++;

    data.forEach((pipeline) => {
        // Customer info
        worksheet.getCell(`A${currentRow}`).value = 'Customer';
        worksheet.getCell(`B${currentRow}`).value = `: ${pipeline.merchantName || '-'}`;
        styleInfoSection(worksheet, currentRow);
        currentRow++;

        worksheet.getCell(`A${currentRow}`).value = 'No Whatsapp';
        worksheet.getCell(`B${currentRow}`).value = `: ${pipeline.phone || '-'}`;
        styleInfoSection(worksheet, currentRow);
        currentRow++;

        // Product info
        const productInfo = pipeline.products?.join('\n') || '-';
        worksheet.mergeCells(`B${currentRow}:${lastColumn}${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = 'Nama Produk';
        worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'top' };
        worksheet.getCell(`B${currentRow}`).value = `: ${productInfo}`;
        worksheet.getCell(`B${currentRow}`).alignment = { wrapText: true, horizontal: 'left', vertical: 'top' };
        styleInfoSection(worksheet, currentRow);
        worksheet.getRow(currentRow).height = Math.max(20, (pipeline.products?.length || 1) * 20); // -> 20 adalah tinggi row default, 20 adalah tinggi setiap baris produk
        currentRow++;

        // Invoice info (if available)
        const invoiceNo = pipeline.noInvoice || '-';
        worksheet.getCell(`A${currentRow}`).value = 'No Invoice';
        worksheet.getCell(`B${currentRow}`).value = `: ${invoiceNo}`;
        styleInfoSection(worksheet, currentRow);
        currentRow++;

        worksheet.getCell(`A${currentRow}`).value = 'Total';
        worksheet.getCell(`B${currentRow}`).value = `: ${pipeline.nominalPipeline}`;
        styleInfoSection(worksheet, currentRow);
        currentRow++;

        // Empty row
        currentRow++;

        const headers = ['No', 'Nama Termin', 'Tanggal Jatuh Tempo', 'Tanggal Bayar', 'Nominal', 'Status Pembayaran'];
        for (let i = 0; i < headers.length; i++) {
            worksheet.getCell(`${String.fromCharCode(66 + i)}${currentRow}`).value = headers[i];
        }
        styleTableHeaders(worksheet, currentRow, totalColumns);
        currentRow++;

        // Termin data
        if (pipeline.paymentTermins && pipeline.paymentTermins.length > 0) {
            pipeline.paymentTermins.forEach((termin: any, index: number) => {
                const terminData = [
                    index + 1,
                    termin.terminName || `Termin ${index + 1}`,
                    termin.dateDue || '-',
                    termin.datePaid || '-',
                    termin.nominalTermin || '-',
                    termin.statusTermin || '-'
                ];
                // Start data from column B
                for (let i = 0; i < terminData.length; i++) {
                    worksheet.getCell(`${String.fromCharCode(66 + i)}${currentRow}`).value = terminData[i];
                }
                styleDataRow(worksheet, currentRow, totalColumns);
                currentRow++;
            });
        }

        currentRow += 2;
    });

    // Set column widths
    setColumnWidths(worksheet, totalColumns);

    exportWorkbook(workbook, `${nameFile}.xlsx`)
}

const setColumnWidths = (worksheet: ExcelJS.Worksheet, totalColumns: number) => {
    worksheet.getColumn('A').width = 15;
    worksheet.getColumn('B').width = 8;  // No column
    worksheet.getColumn('C').width = 25; // Termin Name
    worksheet.getColumn('D').width = 25; // Date Due
    worksheet.getColumn('E').width = 20; // Date Paid
    worksheet.getColumn('F').width = 20; // Nominal
    worksheet.getColumn('G').width = 22; // Status Pembayaran
};

const stylePipelineHeader = (worksheet: ExcelJS.Worksheet, rowNumber: number, totalColumns: number) => {
    const headerRow = worksheet.getRow(rowNumber);

    headerRow.font = {
        name: 'Arial',
        size: 16,
        bold: true,
    };

    headerRow.alignment = {
        horizontal: 'center',
        vertical: 'middle',
    };

};

const stylePeriodHeader = (worksheet: ExcelJS.Worksheet, rowNumber: number, totalColumns: number) => {
    const headerRow = worksheet.getRow(rowNumber);

    headerRow.font = {
        name: 'Arial',
        size: 14,
        bold: true,
    };

    headerRow.alignment = {
        horizontal: 'center',
        vertical: 'middle',
    };

};

const styleInfoSection = (worksheet: ExcelJS.Worksheet, rowNumber: number) => {
    const row = worksheet.getRow(rowNumber);

    row.font = {
        name: 'Arial',
        size: rowNumber === 4 ? 12 : 11, // -> row number 4 adalah total semua nominal pipeline
        bold: true,
    };

};

const styleTableHeaders = (worksheet: ExcelJS.Worksheet, rowNumber: number, totalColumns: number) => {
    for (let i = 0; i < totalColumns; i++) {
        const cellAddress = `${String.fromCharCode(66 + i)}${rowNumber}`;
        const cell = worksheet.getCell(cellAddress);

        cell.font = {
            name: 'Arial',
            size: 12,
            bold: true,
        };

        cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
        };

        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
                argb: 'F6991D',
            },
        };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
    }
};

const styleDataRow = (worksheet: ExcelJS.Worksheet, rowNumber: number, totalColumns: number) => {
    for (let i = 0; i < totalColumns; i++) {
        const cellAddress = `${String.fromCharCode(66 + i)}${rowNumber}`;
        const cell = worksheet.getCell(cellAddress);

        cell.font = {
            name: 'Arial',
            size: 11,
        };

        cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
        };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
                argb: 'FFFFFF',
            },
        };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
    }
};